import { useEffect, useRef, useState } from "react";
import type {
  BuildLocation,
  BuildOptions,
  BuildSelection,
  CardDef,
  Command,
  GameView,
  PlacedCard,
  PlayerView,
  Region,
  ResourceLocation,
  ScoreTrack,
  Seat,
  Town,
} from "../types";
import { resourceMeta } from "../gameMeta";
import { GameCard } from "./Card";
import { OfficialArtwork } from "./OfficialArtwork";
import { ResourceIcon } from "./ResourceIcon";
import {
  useBoardInteraction,
  type ResourceBoardInteraction,
  type TargetBoardInteraction,
} from "./BoardInteraction";
import { changeResourceSlots, resourceLocationSelectionState } from "./ResourcePlanner";

interface Props {
  player: PlayerView;
  score: ScoreTrack;
  definitions: Map<string, CardDef>;
  active: boolean;
  viewer: Seat;
  selectedRegions?: number[];
  onRegion?: (id: number) => void;
  buildSelection?: BuildSelection | null;
  canBuild?: boolean;
  busy?: boolean;
  supply?: GameView["supply"];
  buildOptions?: BuildOptions;
  onBuildCommand?: (command: Command) => void;
  placementResetKey?: string;
}

interface BoardPlacementSnapshot {
  seat: Seat;
  roads: { links: boolean; rechts: boolean };
  towns: Array<{ townId: number; kind: Town["kind"]; slots: Array<number | null> }>;
  regions: Array<{ regionId: number; landscapeCardId: number; expansionId: number | null }>;
}

const PLACEMENT_HIGHLIGHT_MS = 1450;

function captureBoardPlacements(player: PlayerView): BoardPlacementSnapshot {
  return {
    seat: player.seat,
    roads: {
      links: player.principality.open_road_left,
      rechts: player.principality.open_road_right,
    },
    towns: player.principality.towns.map((town) => ({
      townId: town.town_id,
      kind: town.kind,
      slots: town.slots.map((placed) => placed?.instance.instance_id ?? null),
    })),
    regions: [...player.principality.upper_regions, ...player.principality.lower_regions].map((region) => ({
      regionId: region.region_id,
      landscapeCardId: region.landscape_card_id,
      expansionId: region.expansion?.instance.instance_id ?? null,
    })),
  };
}

function newlyPlacedBoardTargets(previous: BoardPlacementSnapshot, current: BoardPlacementSnapshot): string[] {
  const targets = new Set<string>();
  if (!previous.roads.links && current.roads.links) targets.add("road:links");
  if (!previous.roads.rechts && current.roads.rechts) targets.add("road:rechts");

  const previousTowns = new Map(previous.towns.map((town) => [town.townId, town]));
  const previousRegions = new Map(previous.regions.map((region) => [region.regionId, region]));
  const previousPlacedIds = new Set([
    ...previous.towns.flatMap((town) => town.slots.filter((id): id is number => id !== null)),
    ...previous.regions.flatMap((region) => region.expansionId === null ? [] : [region.expansionId]),
  ]);

  current.towns.forEach((town) => {
    const oldTown = previousTowns.get(town.townId);
    if (!oldTown) targets.add(`town-piece:${town.townId}`);
    else if (oldTown.kind === "dorp" && town.kind === "stad") targets.add(`town-piece:${town.townId}`);
    town.slots.forEach((instanceId, slot) => {
      if (instanceId !== null && !previousPlacedIds.has(instanceId)) {
        targets.add(`town-slot:${town.townId}:${slot}`);
      }
    });
  });

  current.regions.forEach((region) => {
    const oldRegion = previousRegions.get(region.regionId);
    if (!oldRegion || oldRegion.landscapeCardId !== region.landscapeCardId) targets.add(`landscape:${region.regionId}`);
    if (region.expansionId !== null && !previousPlacedIds.has(region.expansionId)) {
      targets.add(`region-slot:${region.regionId}`);
    }
  });
  return [...targets];
}

export function PrincipalityBoard({
  player,
  score,
  definitions,
  active,
  viewer,
  selectedRegions,
  onRegion,
  buildSelection,
  canBuild = false,
  busy = false,
  supply,
  buildOptions,
  onBuildCommand,
  placementResetKey,
}: Props) {
  const boardInteraction = useBoardInteraction();
  const own = player.seat === viewer;
  const placementSnapshot = captureBoardPlacements(player);
  const placementSignature = JSON.stringify(placementSnapshot);
  const previousPlacements = useRef<{ resetKey?: string; snapshot: BoardPlacementSnapshot } | undefined>(undefined);
  const [recentPlacements, setRecentPlacements] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    const previous = previousPlacements.current;
    previousPlacements.current = { resetKey: placementResetKey, snapshot: placementSnapshot };
    if (!previous || previous.resetKey !== placementResetKey || previous.snapshot.seat !== placementSnapshot.seat || own) {
      setRecentPlacements((current) => current.size ? new Set() : current);
      return;
    }
    const added = newlyPlacedBoardTargets(previous.snapshot, placementSnapshot);
    if (added.length) {
      setRecentPlacements((current) => new Set([...current, ...added]));
    }
  }, [own, placementResetKey, placementSignature]);
  useEffect(() => {
    if (!recentPlacements.size) return;
    const timer = window.setTimeout(() => setRecentPlacements(new Set()), PLACEMENT_HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [recentPlacements]);
  const resourceInteraction = boardInteraction?.kind === "resources" && boardInteraction.player.seat === player.seat
    ? boardInteraction
    : undefined;
  const regionInteraction = boardInteraction?.kind === "regions" && boardInteraction.seat === player.seat
    ? boardInteraction
    : undefined;
  const placedInteraction = boardInteraction?.kind === "placed_cards" && boardInteraction.seat === player.seat
    ? boardInteraction
    : undefined;
  const townCount = player.principality.towns.length;
  const columns = townCount * 2 + 1;
  const selectedDefinition = buildSelection ? definitions.get(buildSelection.instance.card_id) : undefined;
  const selectedBuildOption = buildSelection ? buildOptions?.cards.find((option) => (
    option.instance_id === buildSelection.instance.instance_id
    && option.from_open === (buildSelection.source === "open")
  )) : undefined;
  const selectedDiscountIsLegal = selectedBuildOption && (buildSelection?.discount_resource
    ? selectedBuildOption.discount_resources.includes(buildSelection.discount_resource)
    : selectedBuildOption.can_build_without_discount);
  const placementMode = Boolean(own && canBuild && !boardInteraction && buildSelection && selectedDefinition && onBuildCommand);
  const pieceMode = Boolean(own && canBuild && !boardInteraction && !placementMode && onBuildCommand);
  const hasCity = player.principality.towns.some((town) => town.kind === "stad");
  const placingLandscapeExpansion = placementMode && selectedDefinition?.kind === "landschapsuitbouw";
  const showUpperOuterRow = hasCity || placingLandscapeExpansion || player.principality.upper_regions.some((region) => region.expansion);
  const showLowerOuterRow = hasCity || placingLandscapeExpansion || player.principality.lower_regions.some((region) => region.expansion);
  const validLocations = placementMode && selectedDefinition
    ? new Set((buildOptions
      ? selectedDiscountIsLegal ? selectedBuildOption.locations : []
      : validBuildLocations(player, selectedDefinition)).map(buildLocationKey))
    : new Set<string>();
  const placeSelectedCard = (location: BuildLocation) => {
    if (!buildSelection || !onBuildCommand) return;
    onBuildCommand({
      type: buildSelection.source === "open" ? "build_open_card" : "build_card",
      instance_id: buildSelection.instance.instance_id,
      location,
      discount_resource: buildSelection.discount_resource,
    });
  };

  return (
    <section className={`principality seat-${player.seat} ${active ? "active-player" : ""} ${placementMode ? "placing-card" : ""} ${resourceInteraction ? "resource-planning" : ""} ${regionInteraction || placedInteraction ? "target-planning" : ""}`}>
      <header className="player-ribbon">
        <div>
          <span className={`seat-dot seat-${player.seat}`} />
          <strong>{player.name || (player.seat === "rood" ? "Rood" : "Blauw")}</strong>
          {own ? <small> jij</small> : null}
        </div>
        <div className="score-track" aria-label="Punten">
          <span title="Zegepunten" className="victory">♛ {score.victory}</span>
          <span title="Kracht">⚔ {score.strength}</span>
          <span title="Handel">⚖ {score.commerce}</span>
          <span title="Vaardigheid">✧ {score.skill}</span>
          <span title="Vooruitgang">⌘ {score.progress}</span>
        </div>
      </header>
      <div className="advantages">
        {score.strength_advantage ? <span>⚔ Krachtvoordeel</span> : null}
        {score.commerce_advantage ? <span>⚖ Handelsvoordeel</span> : null}
        {player.ready ? <span>✓ Klaar</span> : null}
        <span>{player.hand_count} handkaart{player.hand_count === 1 ? "" : "en"}</span>
        {placementMode ? <strong className="placement-instruction">Kies een goud gemarkeerde bouwplaats</strong> : null}
        {resourceInteraction ? <strong className="placement-instruction">{resourceInteraction.title}: kies met − en + op het bord</strong> : null}
        {regionInteraction || placedInteraction ? <strong className="placement-instruction">{(regionInteraction || placedInteraction)!.title}</strong> : null}
      </div>
      <div className="domain-scroll">
        <div className="domain-stage">
          <div
            className="physical-domain"
            data-columns={columns}
            style={{
              gridTemplateColumns: `repeat(${columns}, var(--board-card-size))`,
              gridTemplateRows: `${showUpperOuterRow ? "var(--board-card-size)" : "0px"} var(--board-card-size) var(--board-card-size) var(--board-card-size) ${showLowerOuterRow ? "var(--board-card-size)" : "0px"}`,
            }}
          >
            {player.principality.upper_regions.map((region, index) => (
              <RegionColumn
                key={`upper-${region.region_id}`}
                region={region}
                hidden={player.hidden_region_ids?.includes(region.region_id)}
                row="upper"
                column={index * 2 + 1}
                definitions={definitions}
                selected={regionInteraction ? regionInteraction.selectedIds.includes(region.region_id) : selectedRegions?.includes(region.region_id)}
                onRegion={regionInteraction
                  ? regionInteraction.allowedIds.includes(region.region_id) ? () => regionInteraction.onToggle(region.region_id) : undefined
                  : onRegion ? () => onRegion(region.region_id) : undefined}
                resourceInteraction={resourceInteraction}
                placedInteraction={placedInteraction}
                buildSelection={placementMode ? buildSelection : null}
                buildTarget={validLocations.has(buildLocationKey({ kind: "region", region_id: region.region_id }))}
                onBuild={() => placeSelectedCard({ kind: "region", region_id: region.region_id })}
                landscapeRecentlyPlaced={recentPlacements.has(`landscape:${region.region_id}`)}
                expansionRecentlyPlaced={recentPlacements.has(`region-slot:${region.region_id}`)}
              />
            ))}

            {player.principality.lower_regions.map((region, index) => (
              <RegionColumn
                key={`lower-${region.region_id}`}
                region={region}
                hidden={player.hidden_region_ids?.includes(region.region_id)}
                row="lower"
                column={index * 2 + 1}
                definitions={definitions}
                selected={regionInteraction ? regionInteraction.selectedIds.includes(region.region_id) : selectedRegions?.includes(region.region_id)}
                onRegion={regionInteraction
                  ? regionInteraction.allowedIds.includes(region.region_id) ? () => regionInteraction.onToggle(region.region_id) : undefined
                  : onRegion ? () => onRegion(region.region_id) : undefined}
                resourceInteraction={resourceInteraction}
                placedInteraction={placedInteraction}
                buildSelection={placementMode ? buildSelection : null}
                buildTarget={validLocations.has(buildLocationKey({ kind: "region", region_id: region.region_id }))}
                onBuild={() => placeSelectedCard({ kind: "region", region_id: region.region_id })}
                landscapeRecentlyPlaced={recentPlacements.has(`landscape:${region.region_id}`)}
                expansionRecentlyPlaced={recentPlacements.has(`region-slot:${region.region_id}`)}
              />
            ))}

            {player.principality.towns.map((town, index) => {
              const column = index * 2 + 2;
              return (
                <TownColumn
                  key={town.town_id}
                  town={town}
                  townIndex={index}
                  column={column}
                  seat={player.seat}
                  definitions={definitions}
                  buildSelection={placementMode ? buildSelection : null}
                  validLocations={validLocations}
                  onPlace={placeSelectedCard}
                  placedInteraction={placedInteraction}
                  canUpgrade={pieceMode && (buildOptions ? buildOptions.city_town_ids.includes(town.town_id) : Boolean(supply?.cities))}
                  busy={busy}
                  onUpgrade={() => onBuildCommand?.({ type: "upgrade_city", town_id: town.town_id })}
                  recentPlacements={recentPlacements}
                />
              );
            })}

            {Array.from({ length: Math.max(0, townCount - 1) }, (_, index) => (
              <RoadCard key={`road-${index}`} column={index * 2 + 3} seat={player.seat} built />
            ))}
            <RoadCard
              column={1}
              seat={player.seat}
              built={player.principality.open_road_left}
              edge="links"
              canBuildRoad={pieceMode && (buildOptions ? buildOptions.road_sides.includes("links") : !player.principality.open_road_left && Boolean(supply?.roads))}
              canBuildVillage={pieceMode && (buildOptions ? buildOptions.village_sides.includes("links") : player.principality.open_road_left && Boolean(supply?.villages) && (supply?.region_cards || 0) >= 2)}
              busy={busy}
              onRoad={() => onBuildCommand?.({ type: "build_road", side: "links" })}
              onVillage={() => onBuildCommand?.({ type: "build_village", side: "links" })}
              recentlyPlaced={recentPlacements.has("road:links")}
            />
            <RoadCard
              column={columns}
              seat={player.seat}
              built={player.principality.open_road_right}
              edge="rechts"
              canBuildRoad={pieceMode && (buildOptions ? buildOptions.road_sides.includes("rechts") : !player.principality.open_road_right && Boolean(supply?.roads))}
              canBuildVillage={pieceMode && (buildOptions ? buildOptions.village_sides.includes("rechts") : player.principality.open_road_right && Boolean(supply?.villages) && (supply?.region_cards || 0) >= 2)}
              busy={busy}
              onRoad={() => onBuildCommand?.({ type: "build_road", side: "rechts" })}
              onVillage={() => onBuildCommand?.({ type: "build_village", side: "rechts" })}
              recentlyPlaced={recentPlacements.has("road:rechts")}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function RegionColumn({
  region,
  hidden,
  row,
  column,
  definitions,
  selected,
  onRegion,
  resourceInteraction,
  placedInteraction,
  buildSelection,
  buildTarget,
  onBuild,
  landscapeRecentlyPlaced,
  expansionRecentlyPlaced,
}: {
  region: Region;
  hidden?: boolean;
  row: "upper" | "lower";
  column: number;
  definitions: Map<string, CardDef>;
  selected?: boolean;
  onRegion?: () => void;
  resourceInteraction?: ResourceBoardInteraction;
  placedInteraction?: TargetBoardInteraction;
  buildSelection?: BuildSelection | null;
  buildTarget: boolean;
  onBuild: () => void;
  landscapeRecentlyPlaced: boolean;
  expansionRecentlyPlaced: boolean;
}) {
  const regionRow = row === "upper" ? 2 : 4;
  const expansionRow = row === "upper" ? 1 : 5;
  return (
    <>
      <div
        className={`physical-cell ${landscapeRecentlyPlaced ? "recent-opponent-placement" : ""}`}
        style={{ gridColumn: column, gridRow: regionRow }}
        data-board-field={`landscape:${region.region_id}`}
        data-recent-placement={landscapeRecentlyPlaced ? "opponent" : undefined}
      >
        <RegionTile region={region} hidden={hidden} row={row} selected={selected} onClick={onRegion} />
        {resourceInteraction && !hidden ? (
          <ResourceTileControl
            interaction={resourceInteraction}
            location={{ kind: "region", region_id: region.region_id }}
            label={`${resourceMeta[region.resource].label} ${region.production_number}`}
          />
        ) : null}
      </div>
      <div
        className={`physical-cell expansion-cell ${expansionRecentlyPlaced ? "recent-opponent-placement" : ""}`}
        style={{ gridColumn: column, gridRow: expansionRow }}
        data-board-field={`region-slot:${region.region_id}`}
        data-recent-placement={expansionRecentlyPlaced ? "opponent" : undefined}
      >
        {region.expansion ? (
          <>
            <GameCard
              placed={region.expansion}
              definition={definitions.get(region.expansion.instance.card_id)}
              compact
              selected={placedInteraction?.selectedIds.includes(region.expansion.instance.instance_id)}
              onClick={placedInteraction?.allowedIds.includes(region.expansion.instance.instance_id)
                ? () => placedInteraction.onToggle(region.expansion!.instance.instance_id)
                : undefined}
            />
            {resourceInteraction && region.expansion.instance.card_id === "gold.verborgen_goudmijn" ? (
              <ResourceTileControl
                interaction={resourceInteraction}
                location={{ kind: "hidden_gold_mine", instance_id: region.expansion.instance.instance_id }}
                label="Verborgen goudmijn"
              />
            ) : null}
          </>
        ) : buildTarget && buildSelection ? (
          <CardBuildTarget
            label={`Bouw ${buildSelection.instance.card_id} ${row === "upper" ? "boven" : "onder"} dit landschap`}
            selection={buildSelection}
            definition={definitions.get(buildSelection.instance.card_id)}
            onClick={onBuild}
          />
        ) : null}
      </div>
    </>
  );
}

function TownColumn({
  town,
  townIndex,
  column,
  seat,
  definitions,
  buildSelection,
  validLocations,
  onPlace,
  placedInteraction,
  canUpgrade,
  busy,
  onUpgrade,
  recentPlacements,
}: {
  town: Town;
  townIndex: number;
  column: number;
  seat: Seat;
  definitions: Map<string, CardDef>;
  buildSelection?: BuildSelection | null;
  validLocations: Set<string>;
  onPlace: (location: BuildLocation) => void;
  placedInteraction?: TargetBoardInteraction;
  canUpgrade: boolean;
  busy: boolean;
  onUpgrade: () => void;
  recentPlacements: ReadonlySet<string>;
}) {
  const slots = [
    { slot: 1, row: 1, extra: true, direction: "boven" },
    { slot: 0, row: 2, extra: false, direction: "boven" },
    { slot: 2, row: 4, extra: false, direction: "onder" },
    { slot: 3, row: 5, extra: true, direction: "onder" },
  ];
  return (
    <>
      {slots.map(({ slot, row, extra, direction }) => {
        const visible = town.kind === "stad" || !extra;
        const location: BuildLocation = { kind: "town", town_id: town.town_id, slot };
        const field = `town-slot:${town.town_id}:${slot}`;
        const recentlyPlaced = recentPlacements.has(field);
        return (
          <div
            className={`physical-cell ${recentlyPlaced ? "recent-opponent-placement" : ""}`}
            style={{ gridColumn: column, gridRow: row }}
            data-board-field={field}
            data-recent-placement={recentlyPlaced ? "opponent" : undefined}
            key={slot}
          >
            {visible ? (
              <TownBuildSite
                placed={town.slots[slot]}
                definition={town.slots[slot] ? definitions.get(town.slots[slot]!.instance.card_id) : undefined}
                selection={buildSelection}
                selectedDefinition={buildSelection ? definitions.get(buildSelection.instance.card_id) : undefined}
                target={validLocations.has(buildLocationKey(location))}
                label={`${extra ? "Extra bouwplaats" : "Bouwplaats"} ${direction} ${town.kind === "stad" ? "stad" : "dorp"} ${townIndex + 1}`}
                onClick={() => onPlace(location)}
                placedInteraction={placedInteraction}
              />
            ) : null}
          </div>
        );
      })}
      <div
        className={`physical-cell ${recentPlacements.has(`town-piece:${town.town_id}`) ? "recent-opponent-placement" : ""}`}
        style={{ gridColumn: column, gridRow: 3 }}
        data-board-field={`town-piece:${town.town_id}`}
        data-recent-placement={recentPlacements.has(`town-piece:${town.town_id}`) ? "opponent" : undefined}
      >
        <TownCard town={town} index={townIndex} seat={seat} canUpgrade={canUpgrade} busy={busy} onUpgrade={onUpgrade} />
      </div>
    </>
  );
}

function TownBuildSite({
  placed,
  definition,
  selection,
  selectedDefinition,
  target,
  label,
  onClick,
  placedInteraction,
}: {
  placed: PlacedCard | null;
  definition?: CardDef;
  selection?: BuildSelection | null;
  selectedDefinition?: CardDef;
  target: boolean;
  label: string;
  onClick: () => void;
  placedInteraction?: TargetBoardInteraction;
}) {
  if (target && selection) {
    return (
      <CardBuildTarget
        label={`${selectedDefinition?.id === "progress.raadhuis" ? "Vervang Parochiehuis op" : "Bouw op"} ${label.toLowerCase()}`}
        selection={selection}
        definition={selectedDefinition}
        replacing={Boolean(placed)}
        onClick={onClick}
      />
    );
  }
  if (placed) return (
    <GameCard
      placed={placed}
      definition={definition}
      compact
      selected={placedInteraction?.selectedIds.includes(placed.instance.instance_id)}
      onClick={placedInteraction?.allowedIds.includes(placed.instance.instance_id)
        ? () => placedInteraction.onToggle(placed.instance.instance_id)
        : undefined}
    />
  );
  return <div className="empty-build-site" aria-label={label}><span>+</span><small>{label}</small></div>;
}

function CardBuildTarget({ label, selection, definition, replacing = false, onClick }: {
  label: string;
  selection: BuildSelection;
  definition?: CardDef;
  replacing?: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" className="card-build-target" aria-label={label} onClick={onClick}>
      <GameCard instance={selection.instance} definition={definition} compact explanation={false} />
      <span>{replacing ? "VERVANG HIER" : "BOUW HIER"}</span>
    </button>
  );
}

function ResourceTileControl({ interaction, location, label }: {
  interaction: ResourceBoardInteraction;
  location: ResourceLocation;
  label: string;
}) {
  const state = resourceLocationSelectionState({
    player: interaction.player,
    resources: interaction.resources,
    mode: interaction.mode,
    slots: interaction.slots,
    location,
    incoming: interaction.incoming,
    singleLocation: interaction.singleLocation,
  });
  if (!state.required) return null;
  const change = (delta: -1 | 1) => interaction.onChange(changeResourceSlots({
    player: interaction.player,
    resources: interaction.resources,
    mode: interaction.mode,
    slots: interaction.slots,
    location,
    delta,
    incoming: interaction.incoming,
    singleLocation: interaction.singleLocation,
  }));
  const action = interaction.mode === "payment" ? "betalen" : "opslaan";
  const available = interaction.mode === "payment" ? state.option?.amount : state.option?.capacity;
  const resource = state.option?.resource || interaction.resources[0];
  return (
    <div
      className={`resource-tile-control ${state.selected ? "has-selection" : ""} ${state.eligible ? "eligible" : "unavailable"}`}
      data-resource-source={label}
      title={`${label}: ${state.selected} gekozen, ${available || 0} ${interaction.mode === "payment" ? "beschikbaar" : "vrij"}`}
    >
      <span className="resource-tile-identity">
        <ResourceIcon resource={resource} />
        <b>{label}</b>
      </span>
      <small>{interaction.mode === "payment" ? "VAN DEZE KAART" : "OP DEZE KAART"}</small>
      <div>
        <button
          type="button"
          disabled={!state.canRemove}
          onClick={(event) => { event.stopPropagation(); change(-1); }}
          aria-label={`Eén minder ${action} via ${label}`}
        >−</button>
        <output aria-label={`${state.selected} van ${state.required} gekozen via ${label}`}>{state.selected}/{state.required}</output>
        <button
          type="button"
          disabled={!state.canAdd}
          onClick={(event) => { event.stopPropagation(); change(1); }}
          aria-label={`Eén meer ${action} via ${label}`}
        >+</button>
      </div>
      <em><b>{available || 0}</b> {interaction.mode === "payment" ? "beschikbaar" : "vrij"}</em>
    </div>
  );
}

function RegionTile({ region, hidden = false, row, selected, onClick }: {
  region: Region;
  hidden?: boolean;
  row: "upper" | "lower";
  selected?: boolean;
  onClick?: () => void;
}) {
  if (hidden) {
    return (
      <div className={`region hidden-region ${row}`} role="img" aria-label="Verborgen nieuw landschap; Verkenner kiest nog">
        <span className="hidden-region-mark">?</span>
        <small>Verkenner kiest…</small>
      </div>
    );
  }
  const meta = resourceMeta[region.resource];
  return (
    <button
      type="button"
      className={`region resource-${region.resource} ${row} ${selected ? "selected" : ""}`}
      style={{ ["--resource" as string]: meta.color }}
      onClick={onClick}
      disabled={!onClick}
      aria-pressed={onClick ? Boolean(selected) : undefined}
      aria-label={`${meta.label}, worp ${region.production_number}, ${region.amount} voorraad`}
    >
      <OfficialArtwork assetKey={`landscapes/${region.resource}-${region.production_number}`} className="official-region-art" />
      <span className="production-number">{region.production_number}</span>
      <RegionResourceStock resource={region.resource} amount={region.amount} />
    </button>
  );
}

const STOCK_LEVELS = [0, 1, 2, 3] as const;

export function RegionResourceStock({ resource, amount }: { resource: Region["resource"]; amount: number }) {
  const safeAmount = Number.isFinite(amount) ? Math.min(3, Math.max(0, Math.trunc(amount))) : 0;
  const meta = resourceMeta[resource];
  return (
    <span
      className="region-resource-label"
      aria-label={`${safeAmount} van 3 ${meta.label}`}
      data-resource-stock={resource}
      data-amount={safeAmount}
    >
      <b>{meta.label}</b>
      <strong><span>{safeAmount}</span><small>/3</small></strong>
      <span className="region-stock-track" data-stock-slot-count={STOCK_LEVELS.length} aria-hidden="true">
        {STOCK_LEVELS.map((level) => (
          <span
            className={`region-stock-slot ${level > 0 && level <= safeAmount ? "filled" : ""} ${level === safeAmount ? "current" : ""}`}
            data-stock-slot={level}
            data-current={level === safeAmount ? "true" : "false"}
            data-filled={level > 0 && level <= safeAmount ? "true" : "false"}
            key={level}
          >{level}</span>
        ))}
      </span>
    </span>
  );
}

function TownCard({ town, index, seat, canUpgrade, busy, onUpgrade }: {
  town: Town;
  index: number;
  seat: Seat;
  canUpgrade: boolean;
  busy: boolean;
  onUpgrade: () => void;
}) {
  return (
    <div className={`town-card kind-${town.kind}`} aria-label={`${town.kind === "stad" ? "Stad" : "Dorp"} ${index + 1}`}>
      <div className="piece-fallback town-piece" aria-hidden="true"><i /><span>{town.kind === "stad" ? "♜" : "⌂"}</span></div>
      <OfficialArtwork assetKey={`pieces/${seat}/${town.kind}`} className="official-piece-art" />
      <strong>{town.kind === "stad" ? "STAD" : "DORP"} {index + 1}</strong>
      {canUpgrade && town.kind === "dorp" ? (
        <button type="button" className="town-upgrade-target" disabled={busy} onClick={onUpgrade} aria-label={`Bouw stad op dorp ${index + 1}`}>
          <OfficialArtwork assetKey={`pieces/${seat}/stad`} className="official-piece-art" />
          <span>♜</span><b>STAD BOUWEN</b>
        </button>
      ) : null}
    </div>
  );
}

function RoadCard({
  column,
  seat,
  built,
  edge,
  canBuildRoad,
  canBuildVillage,
  busy,
  onRoad,
  onVillage,
  recentlyPlaced = false,
}: {
  column: number;
  seat: Seat;
  built: boolean;
  edge?: "links" | "rechts";
  canBuildRoad?: boolean;
  canBuildVillage?: boolean;
  busy?: boolean;
  onRoad?: () => void;
  onVillage?: () => void;
  recentlyPlaced?: boolean;
}) {
  const roadContents = (
    <>
      <div className="piece-fallback road-piece" aria-hidden="true"><i /><i /><i /></div>
      <OfficialArtwork assetKey={`pieces/${seat}/straat`} className="official-piece-art" />
      <strong>STRAAT</strong>
    </>
  );
  return (
    <div
      className={`physical-cell road-cell ${recentlyPlaced ? "recent-opponent-placement" : ""}`}
      style={{ gridColumn: column, gridRow: 3 }}
      data-board-field={edge ? `road:${edge}` : undefined}
      data-recent-placement={recentlyPlaced ? "opponent" : undefined}
    >
      {built ? <div className="road-card built">{roadContents}</div> : canBuildRoad ? (
        <button type="button" className="road-card piece-build-target" disabled={busy} onClick={onRoad} aria-label={`Bouw straat ${edge}`}>
          {roadContents}<span>BOUW HIER</span>
        </button>
      ) : <div className="road-card empty-road" aria-label={`Vrije straatplek ${edge || ""}`}><span>straatplek</span></div>}
      {edge && canBuildVillage ? (
        <button
          type="button"
          className={`village-build-target edge-${edge}`}
          disabled={busy}
          onClick={onVillage}
          aria-label={`Bouw dorp ${edge}`}
        >
          <OfficialArtwork assetKey={`pieces/${seat}/dorp`} className="official-piece-art" />
          <span>⌂</span><b>DORP BOUWEN</b>
        </button>
      ) : null}
    </div>
  );
}

export function validBuildLocations(player: PlayerView, definition: CardDef): BuildLocation[] {
  if (definition.kind === "landschapsuitbouw") {
    return [...player.principality.upper_regions, ...player.principality.lower_regions]
      .filter((region) => !region.expansion)
      .map((region) => ({ kind: "region" as const, region_id: region.region_id }));
  }
  if (!["nederzettingsgebouw", "nederzettingseenheid", "stadsgebouw"].includes(definition.kind)) return [];
  return player.principality.towns.flatMap((town) => town.slots.flatMap((placed, slot) => {
    if (definition.kind === "stadsgebouw" && town.kind !== "stad") return [];
    if (town.kind === "dorp" && slot !== 0 && slot !== 2) return [];
    const location = { kind: "town" as const, town_id: town.town_id, slot };
    if (definition.id === "progress.raadhuis") {
      return placed?.instance.card_id === "base.parochiehuis" ? [location] : [];
    }
    return placed ? [] : [location];
  }));
}

function buildLocationKey(location: BuildLocation): string {
  return location.kind === "region" ? `region:${location.region_id}` : `town:${location.town_id}:${location.slot}`;
}

export function allPlaced(player: PlayerView) {
  return [
    ...player.principality.towns.flatMap((town) => town.slots.filter(Boolean).map((placed) => placed!)),
    ...[...player.principality.upper_regions, ...player.principality.lower_regions]
      .map((region) => region.expansion)
      .filter(Boolean)
      .map((placed) => placed!),
  ];
}
